/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import { memo, useCallback, useMemo, useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { Popover } from 'foundations-components/transitional/components/Popover';
import PropTypes from 'prop-types';
import get from 'transmute/get';
import { View } from 'foundations-components/transitional/components/View';
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import ImmutablePropTypes from 'react-immutable-proptypes';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'styled-components';
import { RightIcon } from 'foundations-assets';
import PhoneFromNumberOptions from './PhoneFromNumberOptions';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { getTwilioCallingProvider, getHubSpotCallingProvider } from 'calling-lifecycle-internal/call-provider/operators/callProviderOperators';
import { setPersistedConnectFromNumber, setPersistedFromNumber, setPersistedCallProvider } from 'calling-lifecycle-internal/utils/getLocalCallSettings';
import { PhoneNumberSources } from 'calling-lifecycle-internal/constants/PhoneNumberSources';
import { getSource } from 'calling-lifecycle-internal/operators/from-number/FromNumberGetters';
import { handleSetProviderSettings } from '../../callee-omnibus/utils/getOrCreateSettingsOmnibusStore';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CurrentSettingsWrapper = styled.div.withConfig({
  displayName: "CurrentSettingsInfoActivityCreator__CurrentSettingsWrapper",
  componentId: "sc-119r9up-0"
})(["border-style:solid;border-width:0 0 2px 0;border-color:", ";font-weight:500;"], ({
  theme
}) => `${theme.color['temp-do-not-use-border-primary-disabled']}`);
const CallSettingWrapper = styled.div.withConfig({
  displayName: "CurrentSettingsInfoActivityCreator__CallSettingWrapper",
  componentId: "sc-119r9up-1"
})(["cursor:pointer;background-color:transparent;&.is--disabled{opacity:0.5;}&:hover{background-color:", ";}padding:", ";"], ({
  theme
}) => `${theme.color['fill-secondary-subtle']}`, ({
  theme
}) => `${theme.space['300']} ${theme.space['400']}`);
function CurrentSettingsInfoActivityCreator({
  asyncSettingsOmnibus,
  disabled,
  limits,
  onSelectFromNumber,
  selectedCallProvider,
  selectedFromNumber,
  scopes,
  showFromNumberDropdown,
  isUngatedForProviderSwitching
}) {
  const providerName = useMemo(() => get('name', selectedCallProvider), [selectedCallProvider]);
  const [showPhoneFromNumberOptions, setShowPhoneFromNumberOptions] = useState(false);
  const handleShowPhoneFromNumberOptions = useCallback(() => {
    setShowPhoneFromNumberOptions(true);
  }, []);
  let numberOrProvider = providerName;
  if (selectedFromNumber) {
    if (selectedFromNumber.get('nickname') && selectedFromNumber.get('nickname').length) {
      numberOrProvider = `${selectedFromNumber.get('nickname')} ${selectedFromNumber.get('friendlyName')}`;
    } else if (selectedFromNumber.get('friendlyName')) {
      numberOrProvider = selectedFromNumber.get('friendlyName');
    }
  }
  const handleSelectFromNumber = useCallback(key => {
    const nextFromNumber = key.fromNumber;
    onSelectFromNumber(nextFromNumber);
    let callingProvider;
    if (getSource(nextFromNumber) === PhoneNumberSources.CONNECT_NUMBER) {
      setPersistedConnectFromNumber(nextFromNumber);
      callingProvider = getTwilioCallingProvider();
    } else {
      setPersistedFromNumber(nextFromNumber);
      callingProvider = getHubSpotCallingProvider();
    }
    setPersistedCallProvider(callingProvider);
    handleSetProviderSettings({
      callingProvider,
      fromNumber: nextFromNumber
    });
  }, [onSelectFromNumber]);
  return numberOrProvider ?
  /*#__PURE__*/
  /* @bend-expect trellis-tokenized */
  _jsx(CurrentSettingsWrapper, {
    children: /*#__PURE__*/_jsx(Popover, {
      open: showPhoneFromNumberOptions,
      onOpenChange: () => setShowPhoneFromNumberOptions(false),
      animateOnToggle: true,
      inlineSize: "300px",
      content: {
        body: /*#__PURE__*/_jsx(Tooltip, {
          disabled: !disabled,
          placement: "bottom",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `callSettingsWidget.disableTooltip`
          }),
          children: /*#__PURE__*/_jsx(View, {
            marginBlockStart: "-500",
            marginInline: "-600",
            marginBlockEnd: "0",
            children: /*#__PURE__*/_jsx(PhoneFromNumberOptions, {
              asyncSettingsOmnibus: asyncSettingsOmnibus,
              selectedProviderName: providerName,
              selectedFromNumber: selectedFromNumber,
              hasCallingProvidersCapability: false,
              onSelect: handleSelectFromNumber,
              disabled: disabled,
              scopes: scopes,
              limits: limits,
              isUngatedForProviderSwitching: isUngatedForProviderSwitching
            })
          })
        })
      },
      placement: "right bottom",
      arrowSize: "none",
      children: showFromNumberDropdown &&
      /*#__PURE__*/
      /* @bend-expect trellis-tokenized */
      _jsx(CallSettingWrapper, {
        onClick: handleShowPhoneFromNumberOptions,
        "data-selenium-test": "open-pre-call-popup-from-numbers",
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          justifyContent: "space-between",
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "callSettingsWidget.callFrom",
            options: {
              numberOrProvider
            }
          }), /*#__PURE__*/_jsx(RightIcon, {
            size: "300"
          })]
        })
      })
    })
  }) : null;
}
CurrentSettingsInfoActivityCreator.propTypes = {
  asyncSettingsOmnibus: RecordPropType('AsyncSettingsOmnibusResult').isRequired,
  selectedCallProvider: RecordPropType('CallingProvider'),
  selectedFromNumber: RecordPropType('FromNumber'),
  onSelectFromNumber: PropTypes.func.isRequired,
  scopes: ImmutablePropTypes.map.isRequired,
  limits: ImmutablePropTypes.map.isRequired,
  disabled: PropTypes.bool
};
export default /*#__PURE__*/memo(CurrentSettingsInfoActivityCreator);